import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  UserSquareIcon,
  SettingsIcon,
  DashboardIcon,
  PenToolIcon,
  SparkleIcon,
  RssFeedIcon,
} from './Icons';

// ── Tour step definitions ─────────────────────────────────────────────────────

interface TourStep {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}

const TOUR_STEPS: TourStep[] = [
  {
    icon: <UserSquareIcon className="h-6 w-6" />,
    title: 'Connect your accounts',
    description: (
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          Head to <strong className="text-foreground">Accounts</strong> to link your social media
          profiles. Connected accounts appear in the composer so you can target them when
          publishing.
        </p>
        <p>You can connect multiple accounts per platform and disconnect them at any time.</p>
      </div>
    ),
  },
  {
    icon: <SettingsIcon className="h-6 w-6" />,
    title: 'Define your brand voice',
    description: (
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          Go to <strong className="text-foreground">Settings</strong> to set up your brand identity
          — name, description, tone, writing style, target audience, and content pillars.
        </p>
        <p>
          The more detail you add here, the better the AI will perform when generating or enhancing
          post copy in both <strong className="text-foreground">Write</strong> and{' '}
          <strong className="text-foreground">Compose</strong>.
        </p>
      </div>
    ),
  },
  {
    icon: <DashboardIcon className="h-6 w-6" />,
    title: 'Track everything in Overview',
    description: (
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          <strong className="text-foreground">Overview</strong> is your publishing dashboard. Switch
          between <strong className="text-foreground">List</strong> and{' '}
          <strong className="text-foreground">Calendar</strong> views, and filter posts by status —
          Draft, Scheduled, Published, or Failed.
        </p>
        <p>
          The calendar supports month and week sub-views. Stats at the side give you a quick
          snapshot of post activity for the selected month.
        </p>
      </div>
    ),
  },
  {
    icon: <PenToolIcon className="h-6 w-6" />,
    title: 'Compose and publish posts',
    description: (
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          In <strong className="text-foreground">Compose</strong>, write your post copy in the main
          text area, then select the accounts you want to publish to.
        </p>
        <ul className="list-disc list-inside space-y-1 ml-1">
          <li>
            <strong className="text-foreground">Enhance</strong> — AI rewrites the copy optimized
            for each selected platform
          </li>
          <li>
            <strong className="text-foreground">Media</strong> — attach existing files, upload, or
            generate images
          </li>
          <li>
            <strong className="text-foreground">Per-platform editing</strong> — once accounts are
            selected, each platform gets its own editor with character limits and platform-specific
            options
          </li>
          <li>
            <strong className="text-foreground">Tags & Notes</strong> — internal labels, not
            published
          </li>
          <li>
            <strong className="text-foreground">Preview</strong> — see how the post will look on
            each connected platform
          </li>
          <li>
            <strong className="text-foreground">Post</strong> publishes immediately;{' '}
            <strong className="text-foreground">Schedule</strong> lets you pick a future date and
            time
          </li>
        </ul>
      </div>
    ),
  },
  {
    icon: <SparkleIcon className="h-6 w-6" />,
    title: 'Generate content ideas in Write',
    description: (
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          <strong className="text-foreground">Write</strong> uses AI to generate content through a
          guided flow. Choose one of three modes:
        </p>
        <ul className="list-disc list-inside space-y-1 ml-1">
          <li>
            <strong className="text-foreground">Ideas</strong> — brainstorm standalone post concepts
          </li>
          <li>
            <strong className="text-foreground">Calendar</strong> — plan a full publishing schedule
            across a timeframe
          </li>
          <li>
            <strong className="text-foreground">From Source</strong> — turn inspiration items into
            post ideas
          </li>
        </ul>
        <p>
          Generated ideas can be sent directly to Compose as drafts ready to publish or schedule.
        </p>
      </div>
    ),
  },
  {
    icon: <RssFeedIcon className="h-6 w-6" />,
    title: 'Source ideas in Inspiration',
    description: (
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          <strong className="text-foreground">Inspiration</strong> is a content feed you curate. Add
          sources from:
        </p>
        <ul className="list-disc list-inside space-y-1 ml-1">
          <li>
            <strong className="text-foreground">Twitter/X users</strong> — track competitor or
            industry accounts
          </li>
          <li>
            <strong className="text-foreground">RSS feeds</strong> — pull in blog or news content
          </li>
          <li>
            <strong className="text-foreground">Manual entries</strong> — add any link or note
            directly
          </li>
        </ul>
        <p>
          Toggle between <strong className="text-foreground">Kanban</strong> and{' '}
          <strong className="text-foreground">List</strong> view. Items can be turned into draft
          posts, or saved and forwarded to Write to use as inspiration context when generating
          ideas.
        </p>
      </div>
    ),
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface SocialMediaTourModalProps {
  onDismiss: () => void;
}

export function SocialMediaTourModal({ onDismiss }: SocialMediaTourModalProps) {
  const [step, setStep] = useState(0);
  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      onDismiss();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => setStep((s) => s - 1);

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Social Media quick start guide"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onDismiss}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-2 text-primary">
            {current.icon}
            <h2 className="text-base font-semibold !font-satoshi text-foreground">
              {current.title}
            </h2>
          </div>
          <button
            onClick={onDismiss}
            className="flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Skip tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 min-h-[200px]">{current.description}</div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-2 flex items-center justify-between gap-3">
          {/* Step indicators */}
          <div className="flex items-center gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}`}
                className={cn(
                  'rounded-full transition-all',
                  i === step
                    ? 'w-5 h-1.5 bg-primary'
                    : 'w-1.5 h-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50',
                )}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1">
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            )}
            <Button size="sm" onClick={handleNext} className="gap-1">
              {isLast ? (
                "Let's go"
              ) : (
                <>
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
